/**
 * Resolves the viewer's locale into the values the geodata worker
 * needs to build labels: the label language and script, the ordered
 * list of localized-name tags to probe, and the unit system.
 *
 * Browser language preferences and OpenStreetMap name tags both
 * follow BCP 47, so a preference chain maps directly onto the
 * `name:<language>-<script>` and `name:<language>` properties a
 * feature may carry.
 */


/**
 * Label locale for one stylesheet, as sent to the geodata worker.
 *
 * `languages` lists localized-name tags in preference order, each
 * to be probed as a `name:<tag>` feature property.
 */
export type ResolvedLocale = {
    metric: boolean;
    language: string;
    script: string;
    languages: string[];
};


// Maximizing supplies the script a tag leaves implicit, which is
// what makes name:zh-Hans and name:sr-Cyrl reachable.
const scriptOf = (locale: Intl.Locale): string =>
    locale.script || locale.maximize().script || '';


/**
 * Canonical form of a BCP 47 language tag, or `null` when the value
 * is not a structurally well-formed tag.
 *
 * @param value authored configuration value of unknown type
 * @returns the canonicalized tag, e.g. `cs-CZ` for `cs-cz`
 */
export function canonicalize(value: unknown): string | null {

    if (typeof value !== 'string') return null;

    try {
        return Intl.getCanonicalLocales(value)[0] ?? null;
    } catch {
        return null;
    }
}


/**
 * Resolves the label locale from an explicit BCP 47 override, or
 * from the browser's language preferences when the override is
 * `null`. An override replaces those preferences rather than
 * leading them, so a map renders the same text for every visitor.
 *
 * @param override canonical tag from `mapI18n`, or `null`
 */
export function resolveLocale(override: string | null): ResolvedLocale {

    // node has no navigator, so the unit build resolves to en-Latn-US
    const browser = typeof navigator === 'undefined'
        ? ['en'] : [...navigator.languages];

    const chain = override ? [override] : browser;
    const tags: string[] = [];

    for (const tag of chain) {

        const locale = new Intl.Locale(tag);
        const script = scriptOf(locale);

        if (script) tags.push(locale.language + '-' + script);

        tags.push(locale.language);
    }

    const primary = new Intl.Locale(chain[0]);

    // the unit system follows the region, not the language: en-GB
    // and en-IE are metric, en-US is not
    const region = primary.region || primary.maximize().region;

    return {
        metric: region !== 'US',
        language: primary.language,
        script: scriptOf(primary),
        languages: [...new Set(tags)],
    };
}
